// redux/slices/servicePhotosSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";
import { getJwtToken, clearJwtToken } from "../../Utils/authStorage";

export const MAX_SERVICE_PHOTOS = 5;

/* ===================== HELPERS ===================== */

const normalizeError = (error, fallback) =>
    error?.response?.data || error?.message || fallback;

const handleAuthError = async (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
        try {
            await clearJwtToken();
        } catch { }
    }
};

const sortPhotos = (list = []) =>
    [...list].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

// Абсолютный URL картинки для <img src>
export const buildPhotoUri = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const base = getServerUrl().replace(/\/$/, "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};

/* ===================== THUNKS ===================== */

// Все фото мастера (для списка услуг)
export const fetchAllServicePhotos = createAsyncThunk(
    "servicePhotos/fetchAll",
    async (uid, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error("UID не передан");
            const response = await apiRequest(
                "GET",
                `/service-photos/${encodeURIComponent(uid)}`
            );
            const data = response?.data || response;
            return Array.isArray(data) ? data : [];
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(normalizeError(error, "Fetch all service photos failed"));
        }
    }
);

// Фото одной услуги
export const fetchServicePhotos = createAsyncThunk(
    "servicePhotos/fetchOne",
    async ({ uid, serviceId }, { rejectWithValue }) => {
        try {
            if (!uid || !serviceId) throw new Error("UID и serviceId обязательны");
            const response = await apiRequest(
                "GET",
                `/service-photos/${encodeURIComponent(uid)}/${encodeURIComponent(serviceId)}`
            );
            const data = response?.data || response;
            return { serviceId, photos: Array.isArray(data) ? data : [] };
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(normalizeError(error, "Fetch service photos failed"));
        }
    }
);

// Загрузка (files — массив File из <input type="file">)
export const uploadServicePhotos = createAsyncThunk(
    "servicePhotos/upload",
    async ({ uid, serviceId, files }, { rejectWithValue }) => {
        try {
            if (!uid || !serviceId) throw new Error("UID и serviceId обязательны");

            const list = Array.from(files || []);
            if (list.length === 0) throw new Error("Файлы не выбраны");

            const formData = new FormData();
            list.forEach((file) => formData.append("photos", file));

            const jwt = await getJwtToken();
            const base = getServerUrl().replace(/\/$/, "");

            const res = await axios.post(
                `${base}/service-photos/${encodeURIComponent(uid)}/${encodeURIComponent(serviceId)}`,
                formData,
                {
                    headers: {
                        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
                        // Content-Type НЕ задаём — boundary проставит axios
                    },
                    timeout: 60000,
                }
            );

            const data = res?.data || res;
            return { serviceId, photos: data?.photos || [] };
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(normalizeError(error, "Upload service photos failed"));
        }
    }
);

// Удаление фото
export const deleteServicePhoto = createAsyncThunk(
    "servicePhotos/delete",
    async ({ uid, serviceId, photoId }, { rejectWithValue }) => {
        try {
            const response = await apiRequest(
                "DELETE",
                `/service-photos/${encodeURIComponent(uid)}/${encodeURIComponent(
                    serviceId
                )}/${encodeURIComponent(photoId)}`
            );
            const data = response?.data || response;
            return { serviceId, photos: data?.photos || [] };
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(normalizeError(error, "Delete service photo failed"));
        }
    }
);

// Порядок + обложка
export const reorderServicePhotos = createAsyncThunk(
    "servicePhotos/reorder",
    async ({ uid, serviceId, order, coverId }, { rejectWithValue }) => {
        try {
            const response = await apiRequest(
                "PUT",
                `/service-photos/${encodeURIComponent(uid)}/${encodeURIComponent(serviceId)}/reorder`,
                { order, coverId }
            );
            const data = response?.data || response;
            return { serviceId, photos: data?.photos || [] };
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(normalizeError(error, "Reorder service photos failed"));
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    byServiceId: {}, // { [serviceId]: [photo, ...] }
    status: "idle",
    uploading: false,
    error: null,
};

const setPhotos = (state, serviceId, photos) => {
    state.byServiceId[String(serviceId)] = sortPhotos(photos);
};

/* ===================== SLICE ===================== */

const servicePhotosSlice = createSlice({
    name: "servicePhotos",
    initialState,
    reducers: {
        clearServicePhotos: (state, action) => {
            const serviceId = action.payload;
            if (serviceId == null) state.byServiceId = {};
            else delete state.byServiceId[String(serviceId)];
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchAll
            .addCase(fetchAllServicePhotos.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchAllServicePhotos.fulfilled, (state, action) => {
                state.status = "succeeded";
                const map = {};
                (action.payload || []).forEach((p) => {
                    const key = String(p.serviceId);
                    if (!map[key]) map[key] = [];
                    map[key].push(p);
                });
                Object.keys(map).forEach((key) => {
                    map[key] = sortPhotos(map[key]);
                });
                state.byServiceId = map;
            })
            .addCase(fetchAllServicePhotos.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || action.error?.message;
            })

            // fetchOne
            .addCase(fetchServicePhotos.fulfilled, (state, action) => {
                state.status = "succeeded";
                setPhotos(state, action.payload.serviceId, action.payload.photos);
            })
            .addCase(fetchServicePhotos.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || action.error?.message;
            })

            // upload
            .addCase(uploadServicePhotos.pending, (state) => {
                state.uploading = true;
                state.error = null;
            })
            .addCase(uploadServicePhotos.fulfilled, (state, action) => {
                state.uploading = false;
                setPhotos(state, action.payload.serviceId, action.payload.photos);
            })
            .addCase(uploadServicePhotos.rejected, (state, action) => {
                state.uploading = false;
                state.error = action.payload || action.error?.message;
            })

            // delete
            .addCase(deleteServicePhoto.fulfilled, (state, action) => {
                setPhotos(state, action.payload.serviceId, action.payload.photos);
            })
            .addCase(deleteServicePhoto.rejected, (state, action) => {
                state.error = action.payload || action.error?.message;
            })

            // reorder
            .addCase(reorderServicePhotos.fulfilled, (state, action) => {
                setPhotos(state, action.payload.serviceId, action.payload.photos);
            })
            .addCase(reorderServicePhotos.rejected, (state, action) => {
                state.error = action.payload || action.error?.message;
            })

            .addCase(resetStore, () => initialState);
    },
});

export const { clearServicePhotos } = servicePhotosSlice.actions;
export default servicePhotosSlice.reducer;

/* ===================== SELECTORS ===================== */

export const selectPhotosByServiceId = (state) =>
    state?.servicePhotos?.byServiceId || {};

export const selectServicePhotos = (serviceId) => (state) =>
    state?.servicePhotos?.byServiceId?.[String(serviceId)] || [];

export const selectServiceCover = (serviceId) => (state) => {
    const list = state?.servicePhotos?.byServiceId?.[String(serviceId)] || [];
    return list.find((p) => p.isCover) || list[0] || null;
};

export const selectServicePhotosUploading = (state) =>
    state?.servicePhotos?.uploading || false;